import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type {
  BulkStatementInput,
  DisputeInput,
  Reconciliation,
  ReconciliationFilters,
  StatementInput,
  StatementResult,
} from "./types";

const RECON = ["reconciliation"];

function qs(f: ReconciliationFilters): string {
  const p = new URLSearchParams();
  if (f.carrierId) p.set("carrierId", f.carrierId);
  if (f.periodStart) p.set("periodStart", f.periodStart);
  if (f.periodEnd) p.set("periodEnd", f.periodEnd);
  if (f.status) p.set("status", f.status);
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** GET /v1/commission/reconciliation — per-carrier Expected/Received/gap + status
 *  breakdown + the exceptions surface (Finance, org-wide; the API enforces). */
export function useReconciliation(filters: ReconciliationFilters = {}) {
  const api = useApi();
  return useQuery({
    queryKey: [
      ...RECON,
      filters.carrierId ?? null,
      filters.periodStart ?? null,
      filters.periodEnd ?? null,
      filters.status ?? null,
    ],
    queryFn: () => api.get<Reconciliation>(`/v1/commission/reconciliation${qs(filters)}`),
  });
}

function useReconRefetch() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: RECON });
  };
}

/** PATCH /v1/commission/entries/:id — open / resolve a dispute. */
export function useDispute(entryId: string) {
  const api = useApi();
  const refetch = useReconRefetch();
  return useMutation({
    mutationFn: (input: DisputeInput) => api.patch(`/v1/commission/entries/${entryId}`, input),
    onSettled: refetch,
  });
}

/** POST /v1/commission/statements — enter a single carrier statement (triggers
 *  the matcher); the Received side fed from the console. */
export function useEnterStatement() {
  const api = useApi();
  const refetch = useReconRefetch();
  return useMutation({
    mutationFn: (input: StatementInput) => api.post("/v1/commission/statements", input),
    onSettled: refetch,
  });
}

/** POST /v1/commission/statements/bulk — a lump-sum total over the covered
 *  references; the server allocates proportionally (is_estimated). Returns the
 *  matcher's per-entry verdicts. */
export function useEnterBulkStatement() {
  const api = useApi();
  const refetch = useReconRefetch();
  return useMutation({
    mutationFn: (input: BulkStatementInput) =>
      api.post<StatementResult>("/v1/commission/statements/bulk", input),
    onSettled: refetch,
  });
}
